import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_avatar.scss'

import addSubcomponents from '../utils/addSubcomponents'
import { SizeNS, Props, DivAttributes } from '../utils/types'
import IconBase from './Icon'

type AvatarSize = typeof SizeNS.md | typeof SizeNS.lg | typeof SizeNS.xl

interface AvatarProps extends Props {
  clickable?: boolean
  disabled?: boolean
  size?: AvatarSize
}

interface AvatarIconProps extends Props {
  icon: string
}

interface AvatarPortraitProps extends Props {
  imageUrl: string
}

const cx = bind(styles)

export const Avatar = React.forwardRef<
  HTMLDivElement,
  DivAttributes & AvatarProps
>(
  (
    {
      children,
      className,
      clickable = false,
      disabled = false,
      size = SizeNS.md,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cx(
        'core-avatar',
        `core-avatar--${size}`,
        {
          'core-avatar--disabled': disabled,
          'core-avatar--clickable': !disabled && clickable,
        },
        className
      )}
      {...props}
    >
      {React.Children.only(children)}
    </div>
  )
)

export const Icon = React.forwardRef<
  HTMLDivElement,
  DivAttributes & AvatarIconProps
>(({ className, icon, ...props }, ref) => (
  <div ref={ref} className={cx('core-avatar__icon', className)} {...props}>
    <IconBase icon={icon} />
  </div>
))

export const Label = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-avatar__label', className)} {...props}>
      {children}
    </div>
  )
)

export const Portrait = React.forwardRef<
  HTMLDivElement,
  DivAttributes & AvatarPortraitProps
>(({ className, imageUrl, style, ...props }, ref) => (
  <div
    ref={ref}
    className={cx('core-avatar__portrait', className)}
    style={{
      ...style,
      backgroundImage: `url(${JSON.stringify(imageUrl)})`,
    }}
    {...props}
  />
))

Avatar.displayName = 'Avatar'

Icon.displayName = 'Avatar.Icon'

Label.displayName = 'Avatar.Label'

Portrait.displayName = 'Avatar.Portrait'

export default addSubcomponents(
  {
    Icon,
    Label,
    Portrait,
  },
  Avatar
)
